import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";

/**
 * Reusable auto-search form for Series / Package additions.
 * Mirrors the reference screen: a single search input, live suggestion
 * list, and a quantity + Add-to-Order footer once a row is chosen.
 *
 * Quantity clamp (`Math.max(1, parseInt(...))`) is preserved so the
 * order-line built downstream is always ≥ 1.
 */
export default function AutoSearchAddForm({ items, branch, placeholder, emptyHint, onAdd, testidPrefix, metaFor }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState(1);

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return items.filter((it) => (it.name || "").toLowerCase().includes(needle)).slice(0, 12);
  }, [q, items]);

  const priceForBranch = (row) => {
    if (!row) return 0;
    const ov = Array.isArray(row.branch_overrides) ? row.branch_overrides.find((b) => b.branch_id === branch?.id) : null;
    return Number(ov?.price ?? row.price ?? 0);
  };

  const submit = () => {
    if (!picked) { toast.error("Pick an item first"); return; }
    onAdd(picked, Math.max(1, parseInt(String(qty || 1), 10)));
    setPicked(null); setQ(""); setQty(1);
  };

  return (
    <div className="grid gap-3" data-testid={`${testidPrefix}-form`}>
      <div>
        <Label className="text-xs">Search</Label>
        <div className="relative">
          <Input
            value={picked ? picked.name : q}
            onChange={(e) => { setPicked(null); setQ(e.target.value); }}
            placeholder={placeholder}
            data-testid={`${testidPrefix}-input`}
          />
          {!picked && matches.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-md max-h-64 overflow-auto">
              {matches.map((row) => (
                <button
                  type="button"
                  key={row.id}
                  onClick={() => { setPicked(row); setQ(""); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between"
                  data-testid={`${testidPrefix}-pick-${row.id}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate">{row.name}</span>
                    {metaFor?.(row) && (
                      <span className="block text-[11px] text-muted-foreground truncate" data-testid={`${testidPrefix}-meta-${row.id}`}>
                        {metaFor(row)}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground ml-3 shrink-0">Rp {priceForBranch(row).toLocaleString()}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {!picked && q.trim() === "" && (
          <p className="text-[11px] text-muted-foreground mt-1">{emptyHint}</p>
        )}
        {!picked && q.trim() !== "" && matches.length === 0 && (
          <p className="text-[11px] text-muted-foreground mt-1">No matches for &quot;{q}&quot;.</p>
        )}
      </div>

      {picked && (
        <div className="border border-border rounded-md p-3 bg-muted/20 flex items-center justify-between" data-testid={`${testidPrefix}-selected`}>
          <div>
            <div className="text-sm font-medium">{picked.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{picked.category || ""}</div>
            {metaFor?.(picked) && (
              <div className="text-[11px] font-medium text-sky-800 mt-1 flex items-center gap-1" data-testid={`${testidPrefix}-selected-meta`}>
                <CalendarClock className="w-3 h-3" /> {metaFor(picked)}
              </div>
            )}
          </div>
          <div className="text-sm font-medium">Rp {priceForBranch(picked).toLocaleString()}</div>
        </div>
      )}

      <div className="flex items-end justify-between gap-3 pt-2 border-t border-border">
        <div>
          <Label className="text-xs">Quantity</Label>
          <Input
            type="number"
            min={1}
            value={qty}
            onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
            className="w-24"
            data-testid={`${testidPrefix}-qty`}
          />
        </div>
        <Button onClick={submit} disabled={!picked} data-testid={`${testidPrefix}-add`}>Add to Order</Button>
      </div>
    </div>
  );
}
